import { useEffect, useState, useMemo } from 'react';
import { Button, Divider, Radio, Empty, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import TabletHeader from '../../../components/Operations/TabletHeader';
import ItemCard from '../../../components/Operations/Delivery/ItemCard';
import { useGetOrderLineItemsQuery } from '../../../redux/api/order-line-item.ts';
import { Lineitem } from '../../../interfaces/Ordel.Interface';
import { mergeDeliveryPages } from './mergePages';
import { useBuildingContext } from '../../../context/BuildingContext';
import {
	TABLET_TITLE_STYLE,
	TABLET_BACK_BUTTON_STYLE,
	TABLET_SEARCH_STYLE,
	TABLET_RADIO_GROUP_STYLE,
	TABLET_RADIO_BUTTON_STYLE,
	TABLET_CARD_GRID_STYLE,
} from '../../../components/Operations/tabletStyles';

const Delivery = () => {
	const { buildingId } = useBuildingContext();
	const navigate = useNavigate();
	const [, setLoading] = useState(false);
	const [showMoreButton, setShowMoreButton] = useState(true);
	const [dataSource, setDataSource] = useState<Lineitem[]>([]);
	const [pageNumber, setPageNumber] = useState(1);

	const queryParams = useMemo(
		() => new URLSearchParams(location.search),
		[location.search]
	);

	const search = queryParams.get('search') || '';
	const deliveryStatus = queryParams.get('delivery_status') || '';

	const DeliveryQuery = useMemo(
		() => ({
			ordering: 'required_partner_ship_date',
			page_size: 20,
			search,
			delivery_status: deliveryStatus,
			page: pageNumber,
			machining_status: 'Completed,In Progress',
			quality_status: 'Completed,In Progress',
			// Cancelled and On Hold jobs never ship (PTB-7389).
			is_queues: true,
			created_time_min: '2025-03-01',
			...(buildingId ? { building: buildingId } : {}),
		}),
		[search, deliveryStatus, pageNumber, buildingId]
	);

	const { data, refetch, isFetching, isLoading } = useGetOrderLineItemsQuery(
		DeliveryQuery,
		{
			refetchOnMountOrArgChange: true,
		}
	);

	useEffect(() => {
		setDataSource([]);
		setPageNumber(1);
		setShowMoreButton(true);
	}, [search, deliveryStatus, buildingId]);

	useEffect(() => {
		if (!data) return;

		setDataSource((prevDataSource) =>
			mergeDeliveryPages(prevDataSource, data.results || [])
		);

		setShowMoreButton(Boolean(data?.next));
	}, [data]);

	const onLoadMore = () => {
		setLoading(true);
		setPageNumber((prev) => prev + 1);
	};

	const loadMore = showMoreButton ? (
		<div style={{ textAlign: 'center', marginTop: 16, padding: '8px 0' }}>
			<Button
				size="large"
				style={{ fontSize: 16 }}
				onClick={onLoadMore}
				loading={isFetching || isLoading}
			>
				Load More
			</Button>
		</div>
	) : null;

	return (
		<div className="flex flex-col w-full p-3">
			<TabletHeader
				title="Delivery"
				titleStyle={TABLET_TITLE_STYLE}
				showOnClickButton
				icon={<ArrowLeftOutlined />}
				buttonText="Back"
				actionButtonStyle={TABLET_BACK_BUTTON_STYLE}
				searchStyle={TABLET_SEARCH_STYLE}
				radioGroupStyle={TABLET_RADIO_GROUP_STYLE}
				handleOnClick={() => navigate('/tablet/operations')}
				onSearch={(value) => {
					queryParams.set('search', String(value));
					if (value) {
						queryParams.set('delivery_status', 'All');
					}
					setDataSource([]);
					setPageNumber(1);
					setShowMoreButton(true);
					navigate({ search: queryParams.toString() });
				}}
				onRadioChange={(e) => {
					queryParams.set('delivery_status', String(e.target.value));
					setDataSource([]);
					setPageNumber(1);
					navigate({ search: queryParams.toString() });
					setShowMoreButton(true);
				}}
				radioOptions={
					<>
						<Radio.Button value="Pending" style={TABLET_RADIO_BUTTON_STYLE}>
							Pending
						</Radio.Button>
						<Radio.Button value="Completed" style={TABLET_RADIO_BUTTON_STYLE}>
							Completed
						</Radio.Button>
						<Radio.Button value="All" style={TABLET_RADIO_BUTTON_STYLE}>
							All
						</Radio.Button>
					</>
				}
				radioValue={deliveryStatus}
			/>

			<Divider style={{ marginBottom: 5 }} />

			{dataSource.length === 0 && !isLoading && !isFetching ? (
				<Empty
					image={null}
					description={
						<div>
							<Typography.Title level={4}>
								No Items Ready for Delivery
							</Typography.Title>
							<Typography.Text>
								If you believe your part should be here, please verify that the
								Machine Status and Quality Status are both completed.
							</Typography.Text>
						</div>
					}
				/>
			) : (
				<>
					<div style={TABLET_CARD_GRID_STYLE}>
						{dataSource.map((item) => (
							<ItemCard
								key={item.id}
								item={item}
								refetch={() => {
									setDataSource([]);
									setPageNumber(1);
									setShowMoreButton(true);
									refetch();
								}}
							/>
						))}
					</div>
					{loadMore}
				</>
			)}
		</div>
	);
};

export default Delivery;
